import cs from 'classnames';
import TruncateMarkup from 'react-truncate-markup';
import { format, parseISO } from 'date-fns';
import { ARTICLE_PUBLISHED_DATE_FORMAT } from '../../constants';
import { ARTICLE_URL } from '@constants';
import { Article, ArticleVariant, CardSize, ImageObjectFit } from '@repo/types';
import { formatTag } from '../../../utils/formatTag';
import { cardSizeClass } from '../../../utils/cardSizeClass';
import { Image, ImageVariant } from '../../../common/components/Image';
import { Link } from 'react-router';

type ArticleCardBaseProps = {
  showAuthor?: boolean;
  showImage?: boolean;
  showTag?: boolean;
  showDate?: boolean;
  showSummary?: boolean;
  variant?: ArticleVariant;
  imageObjectFit?: ImageObjectFit;
  /** Predefined card width; `auto`/undefined keeps the fit-to-container default. */
  size?: CardSize;
  prioritize?: boolean;
  style?: React.CSSProperties;
  href?: string;
  /** Fires when the card's link is followed — a surface that overlays the page (GlobalSearch)
   * closes itself on this. The card owns its own anchor, so a caller cannot wrap it in one. */
  onNavigate?: () => void;
};

type ArticleCardProps = ArticleCardBaseProps & {
  article: Article;
};

type ArticleCardContentProps = {
  isOverlay: boolean;
  hasTag: boolean;
  firstTag: string;
  showDate: boolean;
  publishDate: string;
  publishDateFormatted: string;
  title: string;
  author: string;
  showAuthor: boolean;
  summary: string;
  showSummary: boolean;
};

function ArticleCardContent({
  isOverlay,
  hasTag,
  firstTag,
  showDate,
  publishDate,
  publishDateFormatted,
  title,
  author,
  showAuthor,
  summary,
  showSummary,
}: ArticleCardContentProps) {
  const dateEl = showDate && (
    <time className="article-card__date text-3xs" dateTime={publishDate}>
      {publishDateFormatted}
    </time>
  );
  const titleEl = (
    <TruncateMarkup lines={2}>
      <h2 className="article-card__title">{title}</h2>
    </TruncateMarkup>
  );

  // Default variant = the image-with-text-over-gradient overlay card: the photo
  // fills the tile and only the date + headline sit at the bottom over a dark
  // scrim. Tag, author and summary belong to the text-side-by-side layout only.
  if (isOverlay) {
    return (
      <div className="article-card__content">
        {dateEl}
        {titleEl}
      </div>
    );
  }

  return (
    <div className="article-card__content">
      <div className="article-card__tag-date-wrapper">
        {/* The tag reads as a text eyebrow beside the date; the pill overlay on
            the image is reserved for the highlighted article. */}
        {hasTag && <span className="article-card__tag">{formatTag(firstTag)}</span>}
        {dateEl}
      </div>
      {titleEl}
      {author && showAuthor && (
        <footer className="article-card__footer">
          <p className="article-card__author text-3xs">
            <span className="article-card__author-label">by </span>
            <span className="article-card__author-value">{author}</span>
          </p>
        </footer>
      )}
      {summary && showSummary && <p className="article-card__summary small-text">{summary}</p>}
    </div>
  );
}

export function ArticleCard({
  article,
  variant = ArticleVariant.Default,
  showAuthor = false,
  showImage = true,
  showTag = true,
  showDate = true,
  showSummary = false,
  imageObjectFit,
  size,
  prioritize = false,
  style,
  href,
  onNavigate,
}: ArticleCardProps) {
  const { title, publishDate, imageSrc, author, tags, summary, slug } = article;

  const publishDateFormatted = publishDate ? format(parseISO(publishDate), ARTICLE_PUBLISHED_DATE_FORMAT) : '';
  const sizeClass = cardSizeClass('article-card', size);
  const className = cs(
    'article-card',
    {
      'article-card--no-image': !showImage,
      'article-card--no-tag': !showTag,
      'article-card--no-date': !showDate,
      'article-card--default': variant === ArticleVariant.Default,
      'article-card--stacked': variant === ArticleVariant.Stacked,
      'article-card--text-side-by-side': variant === ArticleVariant.TextSideBySide,
    },
    sizeClass
  );

  const linkTo = href ?? `${ARTICLE_URL}/${slug}`;
  const hasTag = tags.length > 0 && !!tags[0] && showTag;

  return (
    <article style={style} className={className}>
      <Link className="article-card__link-wrapper" to={linkTo} onClick={onNavigate}>
        {imageSrc && showImage && (
          <div className="article-card__media-wrapper">
            <Image
              className="article-card__thumbnail"
              variant={ImageVariant.HeroS}
              src={imageSrc}
              alt={title}
              priority={prioritize}
              fallbackToLogo
              // Editor-controlled fit; omit when unset so the SCSS default
              // (object-fit: cover) still applies.
              style={imageObjectFit ? { objectFit: imageObjectFit } : undefined}
              sizes="(max-width: 767px) calc(100vw - 32px), 266px"
            />
          </div>
        )}
        <ArticleCardContent
          isOverlay={variant === ArticleVariant.Default}
          hasTag={hasTag}
          firstTag={tags[0] ?? ''}
          showDate={showDate}
          publishDate={publishDate}
          publishDateFormatted={publishDateFormatted}
          title={title}
          author={author}
          showAuthor={showAuthor}
          summary={summary}
          showSummary={showSummary}
        />
      </Link>
    </article>
  );
}
